// Display names for tools surfaced in chat (tool-call cards, etc.).
// Curated overrides win; anything else falls back to a snake_case → Title Case
// transformation. Keep this list in sync with src/tools/*.ts registrations
// when a tool gets a name that doesn't humanize cleanly.
const CURATED: Record<string, string> = {
  delegate_to_agent: 'Delegate to agent',
  list_agents: 'List agents',
  list_agent_tools: "List agent's tools",
  enable_agent: 'Enable agent',
  disable_agent: 'Disable agent',
  dispatch_subagent: 'Dispatch subagent',

  tool_search: 'Search tools',
  tool_get_schemas: 'Load tool schemas',

  propose_plan: 'Propose plan',
  approve_plan: 'Approve plan',
  reject_plan: 'Reject plan',
  require_approval: 'Request approval',

  todo_write: 'Write to-dos',
  todo_list: 'List to-dos',
  todo_show: 'Show to-do',
  todo_complete: 'Complete to-do',

  get_recent_conversations: 'Recent conversations',
  reflect: 'Reflect',
  update_reflection_timestamp: 'Update reflection timestamp',
  verify: 'Verify',

  eddy_read: 'Read identity',
  eddy_write: 'Update identity',
  eddy_learn: 'Learn a lesson',
  // FRONT-1818: the UI calls this field Custom instructions now. The tool
  // IDS keep their wire names (they are in the registry, the autonomy
  // rules and the capability map); only what a person reads changes.
  system_prompt_read: 'Read custom instructions',
  system_prompt_write: 'Update custom instructions',

  migrate_kv_to_graph: 'Migrate KV → graph',

  jira_search: 'Search Jira',
  jira_get_backlog: 'Jira backlog',
  jira_create_issue: 'Create Jira issue',
  jira_update_issue: 'Update Jira issue',
  jira_create_sprint: 'Create Jira sprint',
  jira_move_to_sprint: 'Move to Jira sprint',

  // Apps (custom HTML pages served at /apps/<slug>/)
  app_publish_html: 'Publish app',
  app_update: 'Update app',
  app_delete: 'Delete app',
  app_list: 'List apps',
  app_get: 'Open app',

  // Schedule / cron
  cron_create: 'Schedule task',
  cron_edit: 'Edit schedule',
  cron_delete: 'Unschedule task',
  cron_info: 'Show schedule',
  cron_list: 'List schedules',
  cron_toggle: 'Toggle schedule',
  cron_run_now: 'Run schedule now',

  // Skills
  skill_create: 'Create skill',
  skill_update: 'Update skill',
  skill_info: 'Show skill',
  skill_list: 'List skills',
  skill_status: 'Skill status',
  run_skill: 'Run skill',

  // Knowledge graph / memory
  graph_search: 'Search memory',
  graph_query: 'Query memory',
  graph_update: 'Update memory',
  graph_link: 'Link memory',
  graph_remove: 'Forget memory',
  graph_dedup: 'Dedupe memory',
  graph_merge: 'Merge memory',
  graph_visualize: 'Visualize memory',
  memory_search: 'Search memory',
  memory_save_document: 'Save document',
  memory_read_document: 'Read document',
  entity_show: 'Show entity',
  relationship_path: 'Trace relationships',
  activity_show: 'Show activity',
  schedule_show: 'Show schedule',

  // Company knowledge bases (FRONT-2101)
  knowledge_bases_list: 'List knowledge bases',
  knowledge_search: 'Search company knowledge',
  knowledge_document_read: 'Read knowledge document',

  // Sketches (FRONT-2101)
  sketch_show: 'Show sketch',
  sketch_read: 'Read sketch',

  // Web & images (FRONT-2101)
  web_search: 'Search the web',
  generate_image: 'Generate image',

  // Files
  file_read: 'Read file',
  file_write: 'Write file',
  file_search: 'Search files',
  file_list: 'List files',
  file_show: 'Show file',
  file_delete: 'Delete file',
  file_catalogue: 'List all files',
  file_view_image: 'View image',

  // Folders (FRONT-2101)
  folder_files_list: 'List folder files',
  folder_read: 'Read folder',
  folder_create: 'Create folder',
  folder_file_move: 'Move file to folder',
  folder_file_remove: 'Remove file from folder',

  // Work board (projects — FRONT-2378 retired `routine` as a container kind,
  // and the old routine-schedule tools with the project-automation trio)
  work_tasks_list: 'List tasks',
  work_task_create: 'Create task',
  work_task_update: 'Update task',
  work_task_delete: 'Delete task',
  work_task_duplicate: 'Duplicate task',
  // FRONT-2423 lap 2: the task window's own read/comment tools.
  work_task_get: 'Read task',
  work_task_comment: 'Comment on task',
  work_projects_list: 'List projects',
  work_project_create: 'Create project',
  work_project_delete: 'Delete project',
  work_project_duplicate: 'Duplicate project',
  work_notes_list: 'List notes',
  work_note_create: 'Create note',
  work_note_update: 'Update note',
  work_note_delete: 'Delete note',
  work_files_list: 'List attached files',
  work_file_attach: 'Attach file',
  work_file_detach: 'Detach file',
  work_project_automations_list: 'List automations',
  work_project_automation_attach: 'Attach automation',
  work_project_automation_detach: 'Detach automation',

  // Retired FRONT-2378 tool names, kept here ONLY so an old chat's tool-call
  // card (a historical record — the literal name a past turn actually
  // called) doesn't fall through to the raw snake_case → Title Case
  // formatter ("Work routine schedule attach"). Never called by anything
  // live; do not remove even though the tools themselves are gone.
  work_routine_create: 'Create routine',
  work_routines_list: 'List routines',
  work_routine_schedules_list: 'List routine schedules',
  work_routine_schedule_attach: 'Attach schedule to routine',
  work_routine_schedule_detach: 'Detach schedule from routine',

  // MCP
  mcp_catalogue: 'Browse MCP catalogue',
  mcp_connect: 'Connect MCP',
  mcp_disconnect: 'Disconnect MCP',
  mcp_tools: 'MCP tools',
  mcp_resources: 'MCP resources',
  mcp_read: 'Read MCP resource',
  mcp_test: 'Test MCP',
  local_mcp_add: 'Add local MCP',
  local_mcp_update: 'Update local MCP',
  local_mcp_remove: 'Remove local MCP',
  local_mcp_list: 'List local MCPs',

  // Autonomy
  autonomy_check: 'Check autonomy',
  autonomy_list: 'List autonomy rules',
  autonomy_set_tier: 'Set autonomy tier',
  autonomy_pause: 'Pause autonomy',
  autonomy_resume: 'Resume autonomy',
  autonomy_rule_create: 'Add autonomy rule',
  autonomy_rule_delete: 'Remove autonomy rule',

  // Misc
  tool_create: 'Create custom tool',
  tool_list_custom: 'List custom tools',
  tool_delete_custom: 'Delete custom tool',
  terminal_exec: 'Run terminal command',
  terminal_session: 'Terminal session',
  read_playbook: 'Read playbook',
  start_onboarding: 'Start onboarding',
  end_onboarding: 'End onboarding',
};

export function humanizeToolName(name: string | null | undefined): string {
  if (!name) return '';
  if (CURATED[name]) return CURATED[name];
  // Generic fallback: split on _, -, or camelCase boundaries, then Title Case.
  const cleaned = name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}
